import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Zap, Calculator, FileDown, TrendingDown, AlertTriangle, Loader2, ArrowRight, Sparkles, CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const POTENCIAS = [1.15, 2.3, 3.45, 4.6, 5.75, 6.9, 10.35, 13.8, 17.25, 20.7];

export const SimuladorPage = () => {
  const { user } = useAuth();

  const [commercializers, setCommercializers] = useState([]);
  const [tipo, setTipo] = useState('luz'); // luz | gas | dual
  const [form, setForm] = useState({
    segment: 'residencial',
    client_name: '',
    client_nif: '',
    client_phone: '',
    client_email: '',
    current_commercializer: '',
    current_tariff_name: '',
    potencia: 6.9,
    ciclo: 'simples',
    consumo_kwh: 200,
    consumo_ponta_kwh: 0,
    consumo_cheia_kwh: 0,
    consumo_vazio_kwh: 0,
    current_total_eur: 0,
    // Gás
    escalao: 1,
    consumo_kwh_gas: 80,
    current_total_gas_eur: 0,
    // Dual
    current_total_luz_eur: 0,
    debito_directo: false,
    factura_electronica: false,
    ok_service: false,
    dual: false,
    // Factura actual detalhada — adaptável por fuel (refactor 2026-05-21)
    // Mantém current_termo_fixo_eur_dia + current_preco_kwh_eur + current_descontos como LUZ (retrocompat)
    current_termo_fixo_eur_dia: '',
    current_preco_kwh_eur: '',
    current_descontos: [],  // descontos LUZ
    // Novos campos Gás (modo Gás ou Dual)
    current_gas_termo_fixo_eur_dia: '',
    current_gas_preco_kwh_eur: '',
    current_gas_descontos: [],
  });
  const [results, setResults] = useState([]);
  const [currentBillDetail, setCurrentBillDetail] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (user?.role === 'angariador') return;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/simulador/commercializers`);
        setCommercializers((r.data || []).filter((c) => c.active));
      } catch (e) {
        toast.error('Erro a carregar comercializadoras');
      }
    })();
  }, [user?.role]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const calc = async () => {
    setCalculating(true);
    setResults([]);
    setSelectedIds([]);
    try {
      const r = await axios.post(`${API_URL}/simulador/calculate`, {
        tipo,
        segment: form.segment,
        // Luz
        ciclo: form.ciclo,
        potencia: Number(form.potencia),
        consumo_kwh: Number(form.consumo_kwh) || 0,
        consumo_ponta_kwh: Number(form.consumo_ponta_kwh) || 0,
        consumo_cheia_kwh: Number(form.consumo_cheia_kwh) || 0,
        consumo_vazio_kwh: Number(form.consumo_vazio_kwh) || 0,
        // Gás
        escalao: Number(form.escalao) || 1,
        consumo_kwh_gas: Number(form.consumo_kwh_gas) || 0,
        // Totais actuais
        current_total_eur: Number(form.current_total_eur) || 0,
        current_total_luz_eur: Number(form.current_total_luz_eur) || 0,
        current_total_gas_eur: Number(form.current_total_gas_eur) || 0,
        debito_directo: form.debito_directo,
        factura_electronica: form.factura_electronica,
        ok_service: form.ok_service,
        dual: tipo === 'dual' ? true : form.dual,
        // Factura actual detalhada (opcional)
        current_termo_fixo_eur_dia: Number(form.current_termo_fixo_eur_dia) || 0,
        current_preco_kwh_eur: Number(form.current_preco_kwh_eur) || 0,
        current_descontos: (form.current_descontos || []).map((d) => ({
          nome: d.nome || '',
          valor: Number(d.valor) || 0,
          base: d.base || 'total',
        })),
        // Factura actual detalhada — campos GÁS (modos Gás ou Dual)
        current_gas_termo_fixo_eur_dia: Number(form.current_gas_termo_fixo_eur_dia) || 0,
        current_gas_preco_kwh_eur: Number(form.current_gas_preco_kwh_eur) || 0,
        current_gas_descontos: (form.current_gas_descontos || []).map((d) => ({
          nome: d.nome || '',
          valor: Number(d.valor) || 0,
          base: d.base || 'total',
        })),
      });
      setResults(r.data?.results || []);
      // Guardar detail da factura actual para uso no PDF
      setCurrentBillDetail(r.data?.current_bill_detail || null);
      const top3 = (r.data?.results || []).slice(0, 3).map((x) => x.tariff_id);
      setSelectedIds(top3);
      if (!r.data?.results?.length) toast.warning('Nenhum tarifário compatível encontrado. Verifique os parâmetros.');
      else toast.success(`${r.data.results.length} tarifário(s) calculado(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro a calcular');
    } finally {
      setCalculating(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((sel) => sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  };

  const generatePdf = async () => {
    if (!selectedIds.length) return toast.error('Selecione pelo menos 1 tarifário');
    if (!form.client_name.trim()) return toast.error('Nome do cliente obrigatório');
    setGenerating(true);
    try {
      const payload = {
        tipo,
        client_name: form.client_name,
        client_nif: form.client_nif,
        client_phone: form.client_phone,
        client_email: form.client_email,
        segment: form.segment,
        input: {
          potencia: Number(form.potencia) || 0,
          ciclo: form.ciclo,
          consumo_kwh: Number(form.consumo_kwh) || 0,
          consumo_kwh_gas: Number(form.consumo_kwh_gas) || 0,
          current_commercializer: form.current_commercializer,
          current_tariff_name: form.current_tariff_name,
          // Factura actual detalhada (Luz)
          current_termo_fixo_eur_dia: Number(form.current_termo_fixo_eur_dia) || 0,
          current_preco_kwh_eur: Number(form.current_preco_kwh_eur) || 0,
          current_descontos: (form.current_descontos || []).map((d) => ({
            nome: d.nome || '', valor: Number(d.valor) || 0, base: d.base || 'total',
          })),
          // Factura actual detalhada (Gás)
          current_gas_termo_fixo_eur_dia: Number(form.current_gas_termo_fixo_eur_dia) || 0,
          current_gas_preco_kwh_eur: Number(form.current_gas_preco_kwh_eur) || 0,
          current_gas_descontos: (form.current_gas_descontos || []).map((d) => ({
            nome: d.nome || '', valor: Number(d.valor) || 0, base: d.base || 'total',
          })),
        },
        results,
        selected_tariff_ids: selectedIds,
        current_total_eur: tipo === 'dual'
          ? ((Number(currentBillDetail?.total_eur) || 0) || (Number(form.current_total_luz_eur) + Number(form.current_total_gas_eur)) || 0)
          : (Number(form.current_total_eur) || 0),
        current_bill_detail: currentBillDetail,
      };
      const r = await axios.post(`${API_URL}/simulador/generate-pdf`, payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `simulacao_${(form.client_name || 'cliente').replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF gerado');
    } catch (e) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setGenerating(false);
    }
  };

  const ciclo = form.ciclo;

  const bestSaving = useMemo(() => {
    return results.reduce((acc, r) => {
      if (r.savings_eur_year != null && r.savings_eur_year > (acc?.savings_eur_year || -Infinity)) return r;
      return acc;
    }, null);
  }, [results]);

  if (user?.role === 'angariador') {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <p className="text-lg font-semibold text-slate-800">Sem acesso</p>
          <p className="text-sm text-slate-500 mt-1">Angariadores não têm acesso ao simulador.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="simulador-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Calculator className="w-6 h-6 text-indigo-500" /> Simulador de Poupança · {tipo === 'gas' ? 'Gás' : tipo === 'dual' ? 'Luz + Gás (Dual)' : 'Eletricidade'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">Compare a fatura actual do cliente com os melhores tarifários activos.</p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-5">
          {/* INPUT */}
          <Card className="col-span-12 lg:col-span-5">
            <CardHeader className="pb-3"><CardTitle className="text-base">1. Dados do Cliente & Fatura Actual</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {/* TIPO SIMULAÇÃO */}
              <div className="flex gap-2">
                {[
                  { v: 'luz', label: '⚡ Luz', color: 'amber' },
                  { v: 'gas', label: '🔥 Gás', color: 'orange' },
                  { v: 'dual', label: '✨ Dual (Luz+Gás)', color: 'indigo' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setTipo(opt.v)}
                    data-testid={`tipo-${opt.v}-btn`}
                    className={`flex-1 px-3 py-2 rounded border text-sm font-medium transition ${tipo === opt.v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Nome</Label>
                  <Input value={form.client_name} onChange={(e) => setField('client_name', e.target.value)} data-testid="client-name-input" />
                </div>
                <div>
                  <Label className="text-xs">NIF</Label>
                  <Input value={form.client_nif} onChange={(e) => setField('client_nif', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Telefone</Label>
                  <Input value={form.client_phone} onChange={(e) => setField('client_phone', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input value={form.client_email} onChange={(e) => setField('client_email', e.target.value)} />
                </div>
              </div>

              <div className="border-t pt-3 space-y-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Segmento</Label>
                    <Select value={form.segment} onValueChange={(v) => setField('segment', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residencial">Residencial</SelectItem>
                        <SelectItem value="empresarial">Empresarial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Comercializadora actual</Label>
                    <Input placeholder="Ex: EDP" value={form.current_commercializer} onChange={(e) => setField('current_commercializer', e.target.value)} />
                  </div>
                </div>

                {/* ====== LUZ (Luz só) ====== */}
                {tipo === 'luz' && (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Potência (kVA)</Label>
                        <Select value={String(form.potencia)} onValueChange={(v) => setField('potencia', Number(v))}>
                          <SelectTrigger data-testid="potencia-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{POTENCIAS.map((p) => <SelectItem key={p} value={String(p)}>{p.toFixed(2)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Ciclo</Label>
                        <Select value={form.ciclo} onValueChange={(v) => setField('ciclo', v)}>
                          <SelectTrigger data-testid="ciclo-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="simples">Simples</SelectItem>
                            <SelectItem value="bi_horario">Bi-horário</SelectItem>
                            <SelectItem value="tri_horario">Tri-horário</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Consumo Luz (kWh/mês)</Label>
                        <Input type="number" value={form.consumo_kwh} onChange={(e) => setField('consumo_kwh', e.target.value)} data-testid="consumo-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Valor mensal Luz pago (€)</Label>
                      <Input type="number" step="0.01" value={form.current_total_eur} onChange={(e) => setField('current_total_eur', e.target.value)} data-testid="current-total-input" />
                    </div>
                    {ciclo === 'bi_horario' && (
                      <div className="grid grid-cols-2 gap-2 bg-amber-50 p-2 rounded">
                        <div>
                          <Label className="text-xs">Consumo Cheia (kWh)</Label>
                          <Input type="number" value={form.consumo_cheia_kwh} onChange={(e) => setField('consumo_cheia_kwh', e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs">Consumo Vazio (kWh)</Label>
                          <Input type="number" value={form.consumo_vazio_kwh} onChange={(e) => setField('consumo_vazio_kwh', e.target.value)} />
                        </div>
                      </div>
                    )}
                    {ciclo === 'tri_horario' && (
                      <div className="grid grid-cols-3 gap-2 bg-amber-50 p-2 rounded">
                        <div>
                          <Label className="text-xs">Ponta (kWh)</Label>
                          <Input type="number" value={form.consumo_ponta_kwh} onChange={(e) => setField('consumo_ponta_kwh', e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs">Cheia (kWh)</Label>
                          <Input type="number" value={form.consumo_cheia_kwh} onChange={(e) => setField('consumo_cheia_kwh', e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs">Vazio (kWh)</Label>
                          <Input type="number" value={form.consumo_vazio_kwh} onChange={(e) => setField('consumo_vazio_kwh', e.target.value)} />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ====== GÁS (Gás só) ====== */}
                {tipo === 'gas' && (
                  <div className="space-y-2.5">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Escalão de Gás</Label>
                        <Select value={String(form.escalao)} onValueChange={(v) => setField('escalao', Number(v))}>
                          <SelectTrigger data-testid="escalao-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Escalão 1 (≤220 kWh/ano)</SelectItem>
                            <SelectItem value="2">Escalão 2 (221-500)</SelectItem>
                            <SelectItem value="3">Escalão 3 (501-1000)</SelectItem>
                            <SelectItem value="4">Escalão 4 (&gt;1000)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Consumo Gás (kWh/mês)</Label>
                        <Input type="number" value={form.consumo_kwh_gas} onChange={(e) => setField('consumo_kwh_gas', e.target.value)} data-testid="consumo-gas-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Valor mensal Gás pago (€)</Label>
                      <Input type="number" step="0.01" value={form.current_total_eur} onChange={(e) => setField('current_total_eur', e.target.value)} data-testid="current-total-gas-input" />
                    </div>
                  </div>
                )}

                {/* ====== DUAL (Luz + Gás) ====== */}
                {tipo === 'dual' && (
                  <div className="space-y-2.5">
                    <div className="bg-indigo-50 border border-indigo-200 rounded p-2.5 space-y-2">
                      <div className="text-xs font-semibold text-indigo-700">⚡ Luz</div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">Potência (kVA)</Label>
                          <Select value={String(form.potencia)} onValueChange={(v) => setField('potencia', Number(v))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{POTENCIAS.map((p) => <SelectItem key={p} value={String(p)}>{p.toFixed(2)}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Consumo (kWh)</Label>
                          <Input type="number" value={form.consumo_kwh} onChange={(e) => setField('consumo_kwh', e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs">Pago (€)</Label>
                          <Input type="number" step="0.01" value={form.current_total_luz_eur} onChange={(e) => setField('current_total_luz_eur', e.target.value)} data-testid="current-total-luz-input" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded p-2.5 space-y-2">
                      <div className="text-xs font-semibold text-orange-700">🔥 Gás</div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">Escalão</Label>
                          <Select value={String(form.escalao)} onValueChange={(v) => setField('escalao', Number(v))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1</SelectItem>
                              <SelectItem value="2">2</SelectItem>
                              <SelectItem value="3">3</SelectItem>
                              <SelectItem value="4">4</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Consumo (kWh)</Label>
                          <Input type="number" value={form.consumo_kwh_gas} onChange={(e) => setField('consumo_kwh_gas', e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs">Pago (€)</Label>
                          <Input type="number" step="0.01" value={form.current_total_gas_eur} onChange={(e) => setField('current_total_gas_eur', e.target.value)} data-testid="current-total-gas-dual-input" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ===== Factura Actual Detalhada — adaptada ao modo (refactor 2026-05-21) ===== */}
                {(() => {
                  const renderBlock = (kind) => {
                    const isGas = kind === 'gas';
                    const headerColor = isGas ? 'bg-orange-50 border-orange-200' : 'bg-yellow-50 border-yellow-200';
                    const accent = isGas ? 'text-orange-700' : 'text-amber-700';
                    const icon = isGas ? '🔥' : '⚡';
                    const label = isGas ? 'Gás' : 'Luz';
                    const termoField = isGas ? 'current_gas_termo_fixo_eur_dia' : 'current_termo_fixo_eur_dia';
                    const precoField = isGas ? 'current_gas_preco_kwh_eur' : 'current_preco_kwh_eur';
                    const descsField = isGas ? 'current_gas_descontos' : 'current_descontos';
                    const descs = form[descsField] || [];
                    return (
                      <div key={kind} className={`border ${headerColor} rounded p-2`}>
                        <div className={`text-xs font-semibold ${accent} mb-2 flex items-center gap-1`}>
                          {icon} Factura {label} actual
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[11px] text-slate-600">Termo fixo (€/dia)</Label>
                            <Input type="number" step="0.0001" placeholder="" value={form[termoField]} onChange={(e) => setField(termoField, e.target.value)} data-testid={`current-${kind}-termo-fixo-input`} />
                          </div>
                          <div>
                            <Label className="text-[11px] text-slate-600">Preço {isGas ? 'gás' : 'energia'} (€/kWh)</Label>
                            <Input type="number" step="0.0001" placeholder="" value={form[precoField]} onChange={(e) => setField(precoField, e.target.value)} data-testid={`current-${kind}-preco-kwh-input`} />
                          </div>
                        </div>
                        <div className="mt-2">
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-[11px] text-slate-600">Descontos actuais ({label}) — opcional</Label>
                            <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setField(descsField, [...descs, { nome: '', valor: '', base: 'total' }])} data-testid={`add-${kind}-discount-btn`}>
                              + Adicionar
                            </Button>
                          </div>
                          {descs.map((d, i) => (
                            <div key={i} className="grid grid-cols-12 gap-1 mb-1 items-end">
                              <div className="col-span-4">
                                <Input className="h-8 text-xs" placeholder="Nome (ex: DD 2%)" value={d.nome} onChange={(e) => { const arr = [...descs]; arr[i] = { ...d, nome: e.target.value }; setField(descsField, arr); }} />
                              </div>
                              <div className="col-span-2">
                                <Input className="h-8 text-xs" type="number" step="0.01" placeholder="%" value={d.valor} onChange={(e) => { const arr = [...descs]; arr[i] = { ...d, valor: e.target.value }; setField(descsField, arr); }} />
                              </div>
                              <div className="col-span-4">
                                <Select value={d.base} onValueChange={(v) => { const arr = [...descs]; arr[i] = { ...d, base: v }; setField(descsField, arr); }}>
                                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="termo_fixo">% sobre Termo fixo</SelectItem>
                                    <SelectItem value="energia">% sobre {isGas ? 'Gás' : 'Energia'}</SelectItem>
                                    <SelectItem value="total">% sobre Total</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-2 text-right">
                                <Button type="button" size="sm" variant="ghost" className="h-8 text-red-600 text-[11px]" onClick={() => setField(descsField, descs.filter((_, j) => j !== i))}>×</Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  };
                  return (
                    <details className="bg-slate-50 border border-slate-200 rounded p-2" data-testid="factura-detalhada-section">
                      <summary className="cursor-pointer text-xs font-medium text-slate-700 hover:text-slate-900" data-testid="factura-detalhada-toggle">
                        📋 Factura actual detalhada (opcional — preenche para teres breakdown completo no PDF)
                      </summary>
                      <div className="mt-2 space-y-2">
                        {(tipo === 'luz' || tipo === 'dual') && renderBlock('luz')}
                        {(tipo === 'gas' || tipo === 'dual') && renderBlock('gas')}
                        <p className="text-[11px] text-slate-500">💡 Se preencheres estes campos, o PDF vai mostrar o breakdown completo da factura ANTES (termo fixo + energia + descontos detalhados) em vez de só o valor total.</p>
                      </div>
                    </details>
                  );
                })()}

                <div className="flex gap-3 flex-wrap pt-1">
                  <label className="flex items-center gap-2 text-xs">
                    <Checkbox checked={form.debito_directo} onCheckedChange={(v) => setField('debito_directo', !!v)} /> Débito Directo
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <Checkbox checked={form.factura_electronica} onCheckedChange={(v) => setField('factura_electronica', !!v)} /> Factura Electrónica
                  </label>
                  <label className="flex items-center gap-2 text-xs" data-testid="ok-service-checkbox">
                    <Checkbox checked={form.ok_service} onCheckedChange={(v) => setField('ok_service', !!v)} /> OK Service / Manutenção
                  </label>
                  {tipo !== 'dual' && (
                    <label className="flex items-center gap-2 text-xs">
                      <Checkbox checked={form.dual} onCheckedChange={(v) => setField('dual', !!v)} /> Dual (Luz+Gás)
                    </label>
                  )}
                </div>
              </div>

              <Button onClick={calc} disabled={calculating} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="calculate-btn">
                {calculating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                Calcular Comparativo
              </Button>
            </CardContent>
          </Card>

          {/* RESULTS */}
          <Card className="col-span-12 lg:col-span-7">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">2. Comparativo & Proposta</CardTitle>
              <Button onClick={generatePdf} disabled={generating || !results.length || !selectedIds.length} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-300" data-testid="generate-pdf-btn">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Gerar PDF ({selectedIds.length})
              </Button>
            </CardHeader>
            <CardContent>
              {results.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Calculator className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Preencha os dados e clique em <b>Calcular Comparativo</b>.</p>
                </div>
              )}

              {bestSaving && (Number(form.current_total_eur) > 0 || tipo === 'dual') && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3 flex items-center gap-3" data-testid="best-saving-card">
                  <Sparkles className="w-7 h-7 text-emerald-600" />
                  <div className="flex-1">
                    <div className="text-xs text-emerald-700 font-medium">Melhor poupança anual</div>
                    <div className="text-lg font-bold text-emerald-800">{bestSaving.savings_eur_year?.toFixed(2)} € / ano · <span className="text-sm font-normal">{bestSaving.commercializer_name} — {bestSaving.tariff_name}</span></div>
                  </div>
                </div>
              )}

              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {results.map((r, idx) => {
                  const sel = selectedIds.includes(r.tariff_id);
                  const saving = r.savings_eur_month;
                  const positive = saving != null && saving > 0;
                  return (
                    <div
                      key={r.tariff_id}
                      className={`border rounded-lg p-3 cursor-pointer transition ${sel ? 'border-indigo-400 bg-indigo-50 shadow-sm' : 'hover:bg-slate-50'}`}
                      onClick={() => toggleSelected(r.tariff_id)}
                      data-testid={`result-row-${r.tariff_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox checked={sel} onCheckedChange={() => toggleSelected(r.tariff_id)} onClick={(e) => e.stopPropagation()} />
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: r.commercializer_color }}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-800">{r.commercializer_name}</span>
                            <span className="text-sm text-slate-500 truncate">· {r.tariff_name}</span>
                            {idx === 0 && <Badge className="bg-amber-500 text-white">Melhor preço</Badge>}
                          </div>
                          {r.calc.applied_discounts?.length > 0 && (
                            <div className="text-[11px] text-slate-500 mt-0.5">Descontos: {r.calc.applied_discounts.join(', ')}</div>
                          )}
                          {r.calc.luz_total_eur != null && r.calc.gas_total_eur != null && (
                            <div className="text-[11px] text-slate-600 mt-1 flex gap-2 flex-wrap">
                              <span className="bg-amber-50 border border-amber-200 text-amber-800 px-1.5 py-0.5 rounded">⚡ Luz {r.calc.luz_total_eur.toFixed(2)}€</span>
                              <span className="bg-orange-50 border border-orange-200 text-orange-800 px-1.5 py-0.5 rounded">🔥 Gás {r.calc.gas_total_eur.toFixed(2)}€</span>
                            </div>
                          )}
                          {r.calc.bonus_monthly_eur > 0 && r.calc.bonus_months > 0 && (
                            <div className="text-[11px] text-amber-700 bg-amber-50 inline-block px-1.5 py-0.5 rounded mt-1">
                              🎁 {r.calc.bonus_label || 'Bónus'}: -{r.calc.bonus_monthly_eur.toFixed(2)}€/mês × {r.calc.bonus_months}m (total {r.calc.bonus_total_eur.toFixed(2)}€)
                            </div>
                          )}
                          {r.permanencia_meses > 0 && <div className="text-[11px] text-slate-400">Permanência: {r.permanencia_meses}m</div>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-lg font-bold text-slate-800">{r.calc.total_eur.toFixed(2)} €</div>
                          <div className="text-[11px] text-slate-500">
                            {r.calc.bonus_monthly_eur > 0 ? `mensal nos primeiros ${r.calc.bonus_months}m (s/ IVA)` : 'mensal s/ IVA'}
                          </div>
                          {r.calc.bonus_monthly_eur > 0 && r.calc.total_no_bonus_eur > 0 && (
                            <div className="text-[11px] text-slate-400 line-through">depois: {r.calc.total_no_bonus_eur.toFixed(2)} €</div>
                          )}
                          {positive && (
                            <div className="text-xs font-semibold text-emerald-600 mt-0.5 flex items-center gap-1 justify-end">
                              <TrendingDown className="w-3 h-3" /> -{saving.toFixed(2)}€/mês
                            </div>
                          )}
                          {saving != null && saving <= 0 && (
                            <div className="text-xs text-rose-500 mt-0.5">+{Math.abs(saving).toFixed(2)}€/mês</div>
                          )}
                          {r.savings_eur_year != null && r.savings_eur_year > 0 && (
                            <div className="text-[11px] text-emerald-700 font-medium">{r.savings_eur_year.toFixed(2)}€ / ano</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {results.length > 0 && (
                <div className="space-y-1.5 mt-3">
                  <div className="text-[11px] text-slate-500 italic flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Clique nos tarifários para incluir/excluir do PDF. Top 3 seleccionados por defeito.
                  </div>
                  <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                    ℹ️ Valores SEM IVA · Os preços de energia e termo fixo já incluem os valores de acesso às redes em vigor estipulados pela <b>ERSE</b>.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SimuladorPage;
